import React from "react";
import { useTranslation } from "../../../../localization/useTranslation.ts";
import { SystemModel } from "../../../../system/systemEdit/SystemEdit.types.ts";
import DotLegalStepHeader from "../../../../common/components/dotLegalStepHeader/DotLegalStepHeader.tsx";
import DataCategoryPicker from "../../../../processingActivity/DataCategoriesStep/dataCategoryPicker/DataCategoryPicker.tsx";
import { DataCategoryViewModel } from "../../../../processingActivity/DataCategoriesStep/DataCategoriesStep.types.ts";
import ProcessingActivityOverviewDialog from "../../../../processingActivity/processingActivityOverviewDialog/ProcessingActivityOverviewDialog.tsx";
import { useSystemDataCategoriesField } from "./SystemDataCategoriesField.hooks.ts";
import { Box } from "@mui/material";

export interface SystemDataCategoriesFieldProps {
    systemId: string;
    system?: SystemModel;
    isLoading: boolean;
    isPreview: boolean;
    errorText?: string;
    disabled: boolean;
}

function SystemDataCategoriesField(props: SystemDataCategoriesFieldProps) {
    const { isLoading, allDataCategories, dataCategories, handleChange, showDataCategoryDialog, setShowDataCategoryDialog } = useSystemDataCategoriesField({
        systemViewModel: props.system,
        systemId: props.systemId,
        isPreview: props.isPreview,
    });
    const { translateString } = useTranslation();

    let dataCategoriesData: Array<DataCategoryViewModel> = [];
    if (allDataCategories) {
        dataCategoriesData = allDataCategories!.map((d) => {
            const foundDataCategory = dataCategories.find((x) => x.dataCategoryId === d.id);
            return {
                name: d.name,
                id: d.id,
                description: d.description,
                classification: d.classification,
                isSelected: foundDataCategory ? true : false,
                disabled: foundDataCategory && !foundDataCategory?.isSystemSelected,
                dataTypeIds: d.dataTypeIds,
                selectedDataTypeIds: [],
                canSpecifyDataTypesForAllDataSubjects: true,
            };
        });
    }

    return (
        <Box sx={{ marginBottom: 3 }}>
            <DotLegalStepHeader
                subHeader={translateString("dataCategoriesStepHeaderSecond")}
                toolTipText={translateString("dataCategoriesOnAssetsHeaderTooltip")}
            >
                {translateString("dataCategoriesOnAssetsHeader")}
            </DotLegalStepHeader>

            {props.errorText && (
                <Box
                    sx={(theme) => ({
                        fontSize: theme.typography.pxToRem(12),
                        color: theme.palette.error.main,
                        textAlign: "center",
                    })}
                >
                    {props.errorText}
                </Box>
            )}

            <DataCategoryPicker
                disableDataTypeSelection
                isLoading={props.isPreview || isLoading || props.isLoading}
                readOnly={props.isPreview || props.disabled}
                allDataCategories={dataCategoriesData}
                onDataCategoryClick={handleChange}
                disabledTooltip={translateString("dataCategoryAssociatedWithProcessingActivity")}
                onDisabledTooltipClick={setShowDataCategoryDialog}
                singleLoadingCategory={props.isPreview}
                sensitiveTooltip={translateString("systemSensitiveDataCategoryInfo")}
                nonSensitiveTooltip={translateString("systemNonSensitiveDataCategoryInfo")}
            />

            {showDataCategoryDialog && (
                <ProcessingActivityOverviewDialog
                    onCloseDialog={() => setShowDataCategoryDialog(undefined)}
                    processingActivities={dataCategories.find((x) => x.dataCategoryId === showDataCategoryDialog)?.processingActivities}
                    header={translateString("systemProcessingsDialogDescription")}
                    numberOfProcessingWithoutaccess={dataCategories.find((x) => x.dataCategoryId === showDataCategoryDialog)?.processingActivitiesWithoutAccess}
                />
            )}
        </Box>
    );
}

export default SystemDataCategoriesField;
